import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hapticTick } from '../../_utils/haptics';
import { stepLabel } from './bailey/format';

/* ------------------------------------------------------------------ icons */
const svg = (children, size = 18, extra = {}) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false" {...extra}>{children}</svg>
);
const stroke = { stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };

export const Icon = {
    Up: ({ size }) => svg(<path d="M10 15.5v-11M5 9l5-5 5 5" {...stroke} strokeWidth={2} />, size),
    Dots: ({ size }) => svg(<><circle cx="4.5" cy="10" r="1.6" fill="currentColor" /><circle cx="10" cy="10" r="1.6" fill="currentColor" /><circle cx="15.5" cy="10" r="1.6" fill="currentColor" /></>, size),
    Chevron: ({ size = 16 }) => svg(<path d="M7.5 4.5L13 10l-5.5 5.5" {...stroke} />, size),
    Back: ({ size = 18 }) => svg(<path d="M12.5 4.5L7 10l5.5 5.5" {...stroke} strokeWidth={2} />, size),
    Tick: ({ size = 14 }) => svg(<path className="bly-tick-path" d="M4.5 10.5l3.5 3.5 7.5-8" {...stroke} strokeWidth={2.2} />, size),
    Plus: ({ size }) => svg(<path d="M10 4.5v11M4.5 10h11" {...stroke} />, size),
    Chat: ({ size }) => svg(<path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h9A1.5 1.5 0 0 1 16 5.5v6a1.5 1.5 0 0 1-1.5 1.5H9l-3.5 3v-3h0A1.5 1.5 0 0 1 4 11.5z" {...stroke} />, size),
    Shield: ({ size }) => svg(<path d="M10 3l6 2.2v4.3c0 3.6-2.5 6.3-6 7.5-3.5-1.2-6-3.9-6-7.5V5.2z" {...stroke} />, size),
    Brush: ({ size }) => svg(<><path d="M13.5 3.5l3 3-6.8 6.8-3-3z" {...stroke} /><path d="M6.7 10.3c-2 0-3.2 1.4-3.2 3.2 0 1.2-.5 2-1 2.5 3.5.5 6.2-.8 6.2-3.7" {...stroke} /></>, size),
    Motion: ({ size }) => svg(<><circle cx="10" cy="10" r="6.5" {...stroke} /><path d="M8.5 7.5v5l4-2.5z" fill="currentColor" /></>, size),
    Pause: ({ size }) => svg(<><circle cx="10" cy="10" r="6.5" {...stroke} /><path d="M8.3 7.5v5M11.7 7.5v5" {...stroke} /></>, size),
    Play: ({ size = 14 }) => svg(<path d="M6.5 4.3v11.4l9-5.7z" fill="currentColor" />, size),
    Stop: ({ size = 14 }) => svg(<rect x="5.5" y="5.5" width="9" height="9" rx="2" fill="currentColor" />, size),
    Clock: ({ size = 14 }) => svg(<><circle cx="10" cy="10" r="6.5" {...stroke} /><path d="M10 6.5V10l2.5 1.7" {...stroke} /></>, size),
    Link: ({ size = 14 }) => svg(<path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.6-2.6a3 3 0 0 0-4.2-4.2l-1 1M11.5 8.5a3 3 0 0 0-4.2 0l-2.6 2.6a3 3 0 0 0 4.2 4.2l1-1" {...stroke} />, size),
    Tag: ({ size = 14 }) => svg(<><path d="M3.5 4.7v4.6l7.2 7.2 5.8-5.8-7.2-7.2H4.7a1.2 1.2 0 0 0-1.2 1.2z" {...stroke} /><circle cx="7.2" cy="7.2" r="1.1" fill="currentColor" /></>, size),
    Info: ({ size = 14 }) => svg(<><circle cx="10" cy="10" r="6.5" {...stroke} /><path d="M10 9.2v4M10 6.7v.1" {...stroke} /></>, size),
    Search: ({ size = 16 }) => svg(<><circle cx="9" cy="9" r="5" {...stroke} /><path d="M13 13l3.5 3.5" {...stroke} /></>, size),
    Close: ({ size = 16 }) => svg(<path d="M5.5 5.5l9 9M14.5 5.5l-9 9" {...stroke} />, size),
    Pin: ({ size = 16, filled }) => svg(<path d="M8 3.5h4l-.6 4.3 2.6 2.4v1.3H6v-1.3l2.6-2.4zM10 11.5v5" {...stroke} fill={filled ? 'currentColor' : 'none'} />, size),
    Sidebar: ({ size = 20 }) => svg(<><rect x="3" y="4" width="14" height="12" rx="2.5" {...stroke} /><path d="M8 4v12" {...stroke} /></>, size),
    Home: ({ size = 18 }) => svg(<path d="M3.8 9.2L10 4l6.2 5.2V15a1.5 1.5 0 0 1-1.5 1.5h-2.9v-4.2H8.2v4.2H5.3A1.5 1.5 0 0 1 3.8 15z" {...stroke} />, size),
    Menu: ({ size = 22 }) => svg(<path d="M3.5 6h13M3.5 10h13M3.5 14h13" {...stroke} />, size),
    Compose: ({ size = 18 }) => svg(<><path d="M9 4.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h8a1.5 1.5 0 0 0 1.5-1.5v-3" {...stroke} /><path d="M13.8 3.7l2.5 2.5-6 6H7.8V9.7z" {...stroke} /></>, size),
    // The front desk: a headset for the job, a handset for a call, a soft form for the soul.
    Headset: ({ size = 18 }) => svg(<><path d="M4.5 11.5V10a5.5 5.5 0 0 1 11 0v1.5" {...stroke} /><rect x="3" y="10.5" width="3.2" height="4.6" rx="1.4" {...stroke} /><rect x="13.8" y="10.5" width="3.2" height="4.6" rx="1.4" {...stroke} /><path d="M15.4 15.1c0 1.5-1.4 2.3-3.4 2.3" {...stroke} /></>, size),
    // Needs you: a booking to make, an email to send.
    Calendar: ({ size = 18 }) => svg(<><rect x="3.5" y="4.5" width="13" height="12" rx="2" {...stroke} /><path d="M3.5 8.5h13M7 3v3M13 3v3" {...stroke} /></>, size),
    Mail: ({ size = 18 }) => svg(<><rect x="3" y="5" width="14" height="10.5" rx="2" {...stroke} /><path d="M3.5 6l6.5 5 6.5-5" {...stroke} /></>, size),
    Phone: ({ size = 18 }) => svg(<path d="M6.5 3h-2A1.5 1.5 0 0 0 3 4.6C3.4 11 9 16.6 15.4 17a1.5 1.5 0 0 0 1.6-1.5v-2l-3.5-1.5-1.8 1.8a10 10 0 0 1-4.5-4.5L9 7.5 7.5 4z" {...stroke} />, size),
    Soul: ({ size = 18 }) => svg(<><path d="M10 3.5c3.6 0 6.5 2.4 6.5 5.9 0 2.2-1.1 3.3-2.4 4.5-1.2 1.1-1.9 2.6-4.1 2.6s-3.2-1.3-4.3-2.6C4.5 12.6 3.5 11.5 3.5 9.4 3.5 5.9 6.4 3.5 10 3.5z" {...stroke} /><path d="M7.2 8.4c.9-1.1 2.2-1.6 3.4-1.2" {...stroke} /></>, size)
};

/* --------------------------------------------------------------- composer */
/**
 * The message box. Grows with what's typed up to a few lines, Enter sends and
 * Shift+Enter adds a line. 16px text so iOS never zooms on focus.
 */
export function Composer({ value, onChange, onSend, busy, placeholder, inputRef, onFocus, onBlur, variant = 'home', autoFocus, ariaLabel = 'Message Bailey' }) {
    const local = useRef(null);
    const ref = inputRef || local;
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
        // A scrollbar only once it's actually full.
        el.style.overflowY = el.scrollHeight > 160 ? 'auto' : 'hidden';
    }, [value, ref]);
    const can = !busy && !!String(value || '').trim();
    const send = () => { if (can) { hapticTick(); onSend(); } };
    return (
        <div className={`bly-composer is-${variant}`}>
            <textarea
                ref={ref}
                rows={1}
                className="bly-input"
                value={value}
                placeholder={placeholder}
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button type="button" className="bly-send" onClick={send} disabled={!can} aria-label="Send">
                <Icon.Up size={18} />
            </button>
        </div>
    );
}

/* ------------------------------------------------------------------- menu */
/**
 * The "…" menu: the things that used to sit in the header (Style Bailey,
 * Motion) and the ways around (conversations, how Bailey works).
 */
export function MoreMenu({ items, label = 'More' }) {
    const [open, setOpen] = useState(false);
    const wrap = useRef(null);
    const button = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); if (button.current) button.current.focus(); } };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (
        <div className="bly-menu" ref={wrap}>
            <button
                ref={button}
                type="button"
                className={`bly-iconbtn${open ? ' is-on' : ''}`}
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => { hapticTick(); setOpen((o) => !o); }}
            >
                <Icon.Dots size={20} />
            </button>
            {open && (
                <div className="bly-menu-pop" role="menu">
                    {items.filter(Boolean).map((it) => (
                        <button
                            key={it.label}
                            type="button"
                            role="menuitem"
                            className="bly-menu-item"
                            onClick={() => { setOpen(false); it.onClick(); }}
                        >
                            <span className="bly-menu-icon">{it.icon}</span>
                            <span>{it.label}</span>
                            {it.hint && <small>{it.hint}</small>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------- work steps */
/**
 * What Bailey did to get the answer, from the stream's own tool events.
 * While it works, the current step pulses; once the answer lands the list
 * folds to one quiet line that opens again on a tap.
 */
export function WorkSteps({ steps: raw, working }) {
    const [open, setOpen] = useState(false);
    if (!raw || !raw.length) return null;
    // The same check twice (July's takings, then August's) reads as one step.
    const steps = [];
    raw.forEach((s) => {
        const label = stepLabel(s);
        const at = steps.findIndex((x) => stepLabel(x) === label);
        if (at === -1) steps.push(s);
        else if (!s.done) steps[at] = s;
    });
    const folded = !working && !open;
    if (folded) {
        const done = steps.filter((s) => s.done);
        const text = done.length <= 1 ? stepLabel(done[0] || steps[0]) : `${stepLabel(done[0])} and ${done.length - 1} more`;
        return (
            <button type="button" className="bly-steps-fold" onClick={() => { hapticTick(); setOpen(true); }} aria-expanded="false">
                <span className="bly-steps-tick"><Icon.Tick size={12} /></span>
                <span>{text}</span>
                {done.length > 1 && <Icon.Chevron size={13} />}
            </button>
        );
    }
    return (
        <div className="bly-steps" aria-live="polite">
            {steps.map((s, i) => (
                <div key={i} className={`bly-step${s.done ? ' is-done' : ' is-now'}`}>
                    {s.done
                        ? <span className="bly-steps-tick"><Icon.Tick size={12} /></span>
                        : <span className="bly-step-dot" aria-hidden="true" />}
                    <span>{stepLabel(s)}{s.done ? '' : '…'}</span>
                </div>
            ))}
            {!working && (
                <button type="button" className="bly-steps-hide" onClick={() => setOpen(false)}>Hide</button>
            )}
        </div>
    );
}
